import {useEffect, useRef, useState} from "react";
import s from "./LocationCard.module.scss";
import ImageWithPlaceholder from "../Image/ImageWithPlaceholder";
import {useNavigate} from "react-router-dom";

const LocationCarousel = ({locations = []}) => {

    const navigate = useNavigate();

    const [itemsPerView, setItemsPerView] = useState(4);
    const [index, setIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    /*
    |--------------------------------------------------------------------------
    | Responsive
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const handleResize = () => {

            if (window.innerWidth < 576) {

                // Mobile: 4 compact cards in one row
                setItemsPerView(4);

            } else if (window.innerWidth < 992) {

                setItemsPerView(3);

            } else {

                // Desktop
                setItemsPerView(4);

            }
        };

        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Maximum position
    |--------------------------------------------------------------------------
    */

    const maxIndex = Math.max(
        0,
        locations.length - itemsPerView
    );


    /*
    |--------------------------------------------------------------------------
    | Reset index when responsive size changes
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setIndex(prev =>
            Math.min(prev, maxIndex)
        );

    }, [maxIndex]);


    /*
    |--------------------------------------------------------------------------
    | Navigation
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        setIndex(prev =>
            Math.min(prev + 1, maxIndex)
        );

    };

    const handlePrev = () => {

        setIndex(prev =>
            Math.max(prev - 1, 0)
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Location click
    |--------------------------------------------------------------------------
    */

    const handleLocationClick = (id, name, slug) => {

        const breadcrumbItems = [
            {
                name: "ホーム",
                link: "/"
            },
            {
                name: "ケアンズ以外",
                link: "/categories/location"
            },
            {
                name,
                link: ""
            }
        ];

        navigate(`/tours/location/${slug}`, {
            state: {
                breadcrumbItems
            }
        });

    };


    /*
    |--------------------------------------------------------------------------
    | Swipe
    |--------------------------------------------------------------------------
    */

    const startX = useRef(0);
    const moved = useRef(false);

    const onStart = (e) => {

        setIsDragging(true);

        moved.current = false;

        startX.current =
            e.touches
                ? e.touches[0].clientX
                : e.clientX;

    };


    const onMove = (e) => {

        if (!isDragging) {
            return;
        }

        const currentX =
            e.touches
                ? e.touches[0].clientX
                : e.clientX;

        if (
            Math.abs(startX.current - currentX) > 5
        ) {
            moved.current = true;
        }

    };


    const onEnd = (e) => {

        if (!isDragging) {
            return;
        }

        setIsDragging(false);

        const endX =
            e.changedTouches
                ? e.changedTouches[0].clientX
                : e.clientX;

        const diff = startX.current - endX;

        if (!moved.current) {
            return;
        }

        if (diff > 30) {
            handleNext();
        }

        if (diff < -30) {
            handlePrev();
        }

    };


    /*
    |--------------------------------------------------------------------------
    | Empty
    |--------------------------------------------------------------------------
    */

    if (!locations.length) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | Indicator pages
    |--------------------------------------------------------------------------
    */

    const indicatorCount =
        Math.max(1, locations.length - itemsPerView + 1);


    return (
        <div className={s.carousel}>

            <div
                className={s.viewport}

                onMouseDown={onStart}
                onMouseMove={onMove}
                onMouseUp={onEnd}

                onMouseLeave={() => {
                    if (isDragging) {
                        setIsDragging(false);
                    }
                }}

                onTouchStart={onStart}
                onTouchMove={onMove}
                onTouchEnd={onEnd}
            >

                <div
                    className={s.inner}

                    style={{
                        transform:
                            `translateX(-${index * (100 / itemsPerView)}%)`,

                        transition: isDragging
                            ? "none"
                            : "transform 0.5s ease"
                    }}
                >

                    {locations.map((item) => (

                        <div
                            key={item.id}
                            className={s.item}

                            style={{
                                flex: `0 0 ${100 / itemsPerView}%`
                            }}

                            onClick={() =>
                                handleLocationClick(
                                    item.id,
                                    item.name,
                                    item.slug
                                )
                            }
                        >

                            <div className={s.card}>

                                <ImageWithPlaceholder
                                    src={item.image}
                                    alt={item.name}
                                />

                                <div className={s.overlay}>
                                    {item.name}
                                </div>

                            </div>

                        </div>

                    ))}

                </div>

            </div>


            {/* DESKTOP ARROWS */}

            {maxIndex > 0 && (
                <>
                    <button
                        className={`${s.prev} ${
                            index === 0 ? s.disabled : ""
                        }`}
                        onClick={handlePrev}
                        disabled={index === 0}
                    >
                        ‹
                    </button>

                    <button
                        className={`${s.next} ${
                            index === maxIndex ? s.disabled : ""
                        }`}
                        onClick={handleNext}
                        disabled={index === maxIndex}
                    >
                        ›
                    </button>
                </>
            )}


            {/* INDICATORS */}

            {maxIndex > 0 && (
                <div className={s.indicators}>

                    {Array.from({
                        length: indicatorCount
                    }).map((_, pageIndex) => (

                        <button
                            key={pageIndex}

                            className={
                                pageIndex === index
                                    ? s.activeIndicator
                                    : s.indicator
                            }

                            onClick={() =>
                                setIndex(pageIndex)
                            }

                            aria-label={`Go to destination ${pageIndex + 1}`}
                        />

                    ))}

                </div>
            )}

        </div>
    );
};

export default LocationCarousel;